import {useEffect} from 'react'
import {errorHandler} from '@utilities/helpers.ts'
import {useChatbotStore} from '@features/chatbot/store/chatbotStore.ts'
import {CHATBOT_SOURCES, CHATBOT_CANCELLATION_REASONS} from '@features/chatbot/types.ts'
import {useAuthStore} from '@features/user/store/store.ts'
import {httpCancelShoot} from '@features/chatbot/services/cancelShoot.http.ts'

const REASSIGN_REASON_MAP: Record<string, number> = {
    creator_dont_matching_availabilities: CHATBOT_CANCELLATION_REASONS.CREATOR_DONT_MATCHING_AVAIBILITIES,
    creator_not_available_anymore: CHATBOT_CANCELLATION_REASONS.CREATOR_NOT_AVAILABLE_ANYMORE,
    creator_located_far_away: CHATBOT_CANCELLATION_REASONS.CREATOR_LOCATED_FAR_AWAY,
    creator_dont_feel_comfortable: CHATBOT_CANCELLATION_REASONS.CREATOR_DONT_FEEL_COMFORTABLE,
    creator_accepted_mistake: CHATBOT_CANCELLATION_REASONS.CREATOR_ACCEPTED_MISTAKE,
    host_rescheduled_3_times: CHATBOT_CANCELLATION_REASONS.HOST_RESCHEDULED_3_TIMES
}

interface CancelValues {
    reason_id: number | null
    by?: string
    notes?: string
}

interface CancelShootProps {
    flowSource: string
    steps?: Record<string, {value: string}>
    cancelBy?: string
}

export function CancelShoot({flowSource, steps, cancelBy}: CancelShootProps) {
    const accessToken = useAuthStore(store => store.accessToken)
    const selectedShoot = useChatbotStore(store => store.selectedAssignment)
    const cancellationReason = useChatbotStore(store => store.cancellationReason)
    const addStepToHistory = useChatbotStore(store => store.addStepToHistory)

    useEffect(() => {
        onCancelShootResults()
    }, [])

    function getCancelValues(flowsSource: string, steps?: Record<string, {value: string}>): CancelValues {
        if (!flowsSource) {
            return {
                reason_id: cancellationReason.id ? Number(cancellationReason.id) : null,
                by: cancelBy
            }
        }
        switch (flowsSource) {
            case 'MB1.2_pull':
                return {
                    reason_id: 15,
                    by: 'host',
                    notes: 'Placeholder notes' // TODO: get notes from user
                }
            case 'MB1.3_pull':
                return {
                    reason_id: 10,
                    by: 'host',
                    notes: 'Placeholder notes' // TODO: get notes from user
                }
            case 'MB1.4_reassign': {
                const reasonId =
                    cancellationReason.id && typeof cancellationReason.id === 'string'
                        ? REASSIGN_REASON_MAP[cancellationReason.id] || CHATBOT_CANCELLATION_REASONS.CREATOR_OTHER
                        : CHATBOT_CANCELLATION_REASONS.CREATOR_OTHER
                return {
                    reason_id: reasonId,
                    by: cancelBy || 'creator'
                }
            }
            case 'MB1.4_pull':
                if (steps?.['MB1.4_options']?.value?.includes(';')) {
                    const reason_source = steps['MB1.4_options'].value.split(';')[1]

                    return {
                        reason_id: parseInt(steps[reason_source].value.split(' ')[0]),
                        by: steps[reason_source].value.split(' ')[1],
                        notes: steps['MB1.4_notes_options']?.value
                    }
                } else {
                    return {
                        reason_id: parseInt(steps?.['MB1.4_options']?.value?.split(' ')[0] || '0'),
                        by: steps?.['MB1.4_options']?.value?.split(' ')[1] || 'host',
                        // For the notes, a unique exception is used for the "no show" option, where the notes are taken from its ticket flow
                        notes:
                            steps?.['MB1.4_options']?.value !== '13 host'
                                ? steps?.['MB1.4_notes_options']?.value
                                : steps?.['MB1.4_noshow_notes_input']?.value
                    }
                }
            default:
                return {
                    reason_id: cancellationReason.id ? Number(cancellationReason.id) : null,
                    by: cancelBy || 'host'
                }
        }
    }

    const onCancelShootResults = async () => {
        if (!accessToken || !selectedShoot?.id) {
            console.error('Missing required data for shoot cancellation')
            return
        }

        try {
            const cancelValues = getCancelValues(flowSource, steps)
            const sanitizedValues = {
                ...cancelValues,
                reason_id: cancelValues.reason_id ?? undefined
            }
            await httpCancelShoot({
                assignmentId: selectedShoot.id,
                values: sanitizedValues
            })
            addStepToHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.CHATBOT,
                text: '[Shoot Canceled] Thank you!'
            })
        } catch (e) {
            errorHandler(e)
            addStepToHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.CHATBOT,
                text: '[Shoot Canceled Error] Thank you!'
            })
        }
    }

    // This won't render as replace is always set to true
    return (
        <div>
            <p>{'Thank you!'}</p>
        </div>
    )
}
